import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { IoArrowBack, IoChevronBack, IoChevronForward, IoList, IoAdd, IoArrowUp } from 'react-icons/io5';
import { useBooks } from '../context/BooksContext';
import { useLibrary } from '../context/LibraryContext';
import { useUser } from '../context/UserContext';
import Comments from '../components/Comments';
import StarRating from '../components/StarRating';
import ReaderSettings from '../components/ReaderSettings';
import ReportButton from '../components/ReportButton';
import ShareButton from '../components/ShareButton';
import Bookmarks from '../components/Bookmarks';
import Recommendations from '../components/Recommendations';
import Confetti from '../components/Confetti';
import ChapterReactions from '../components/ChapterReactions';
import TipAuthor from '../components/TipAuthor';
import AdBanner from '../components/AdBanner';
import BoostBook from '../components/BoostBook';
import LikeAnimation from '../components/LikeAnimation';
import { estimateReadingTime, formatReadingTime } from '../utils/readingTime';
import { useReadingLists } from '../context/ReadingListContext';
import { motion, AnimatePresence } from 'framer-motion';
import NotFound from './NotFound';
import './BookReader.css';

function renderRichText(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/^## (.*)$/gm, '<h2 class="reader-inline-title">$1</h2>')
    .replace(/^> (.*)$/gm, '<blockquote class="reader-quote">$1</blockquote>')
    .replace(/^---$/gm, '<hr class="reader-separator" />')
    .replace(/\n/g, '<br />');
}

export default function BookReader() {
  const { bookId } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const navigate = useNavigate();
  const { getBookById, likeBook } = useBooks();
  const { addToLibrary, isInLibrary, updateProgress, getProgress } = useLibrary();
  const { user } = useUser();

  const book = getBookById(bookId);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [showChapterList, setShowChapterList] = useState(false);
  const [liked, setLiked] = useState(false);
  const { myLists, addBookToList } = useReadingLists();
  const [showAddToList, setShowAddToList] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [turnDirection, setTurnDirection] = useState('next');

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!book) return;
    const chParam = searchParams.get('ch');
    if (chParam !== null) {
      const chIndex = parseInt(chParam) || 0;
      setCurrentChapter(chIndex);
      if (user) updateProgress(bookId, chIndex);
    } else {
      const prog = user ? getProgress(bookId) : -1;
      setCurrentChapter(prog >= 0 ? prog : 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsSpeaking(window.speechSynthesis && window.speechSynthesis.speaking);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  const minSwipeDistance = 80;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) < minSwipeDistance) return;
    const chapters = book?.chapters || [];
    const isFirstCh = currentChapter === 0;
    const isLastCh = currentChapter === chapters.length - 1;

    if (distance > 0 && !isLastCh) {
      setTurnDirection('next');
      goToChapter(currentChapter + 1);
    } else if (distance < 0 && !isFirstCh) {
      setTurnDirection('prev');
      goToChapter(currentChapter - 1);
    }
  };

  if (!book) {
    return <NotFound />;
  }

  const chapter = book.chapters[currentChapter];
  const isFirst = currentChapter === 0;
  const isLast = currentChapter === book.chapters.length - 1;

  const goToChapter = (index) => {
    setTurnDirection(index > currentChapter ? 'next' : 'prev');
    setCurrentChapter(index);
    if (user) updateProgress(bookId, index);
    setShowChapterList(false);
    window.scrollTo(0, 0);
  };

  const handleLike = () => {
    if (!user) return;
    if (!liked) {
      likeBook(bookId);
      setLiked(true);
      if (!isInLibrary(bookId)) addToLibrary(book);
    }
  };

  const handleTTS = () => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else if (chapter) {
      const chapterName = chapter.title || 'Chapitre ' + (currentChapter + 1);
      const clean = chapterName + '. ' + (chapter.content || '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/_(.*?)_/g, '$1')
        .replace(/^## /gm, '')
        .replace(/^> /gm, '')
        .replace(/^---$/gm, '');
      const utterance = new SpeechSynthesisUtterance(clean);
      utterance.lang = 'fr-FR';
      utterance.rate = 0.9;
      utterance.onend = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
      setIsSpeaking(true);
    }
  };

  return (
    <div className="reader-page">
      <Confetti active={showConfetti} onDone={() => setShowConfetti(false)} />

      <div className="reader-header">
        <button className="reader-back" onClick={() => navigate('/book/' + bookId + (from ? '?from=' + from : ''))}>
          <IoArrowBack />
        </button>
        <div className="reader-header-info">
          <h3 className="reader-header-title">{book.title}</h3>
          <p className="reader-header-chapter">{chapter ? chapter.title : ''}</p>
        </div>
        {user && <LikeAnimation liked={liked} onLike={handleLike} />}
        {user && (
          <button className="reader-action" onClick={() => setShowAddToList(!showAddToList)}>
            <IoAdd />
          </button>
        )}
        <ReaderSettings />
        {user && <ReportButton type="book" targetId={bookId} targetTitle={book?.title} />}
        <ShareButton title={book?.title} />
        {user && book && chapter && <Bookmarks bookId={bookId} chapterId={chapter.id} chapterTitle={chapter.title} />}
        <button className="reader-action" onClick={() => setShowChapterList(!showChapterList)}>
          <IoList />
        </button>
        <button className={'reader-action ' + (isSpeaking ? 'reader-action-active' : '')} onClick={handleTTS} title="Lecture audio">
          {isSpeaking ? '⏹' : '🔊'}
        </button>
      </div>

      {showAddToList && user && (
        <div className="reader-chapters-overlay" onClick={() => setShowAddToList(false)}>
          <div className="reader-chapters-panel" onClick={e => e.stopPropagation()}>
            <h3 className="reader-chapters-title">Ajouter à une liste</h3>
            {myLists.length > 0 ? (
              myLists.map(list => (
                <button key={list.id} className="reader-chapter-item" onClick={() => { addBookToList(list.id, book); setShowAddToList(false); }}>
                  <span style={{ fontSize: 20 }}>{list.emoji}</span>
                  {list.name}
                  <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.4 }}>{list.books.length} livres</span>
                </button>
              ))
            ) : (
              <p style={{ textAlign: 'center', padding: 20, opacity: 0.4, fontSize: 14 }}>
                Tu n'as pas encore de liste. Crée-en une depuis l'onglet Listes !
              </p>
            )}
          </div>
        </div>
      )}

      {showChapterList && (
        <div className="reader-chapters-overlay" onClick={() => setShowChapterList(false)}>
          <div className="reader-chapters-panel" onClick={e => e.stopPropagation()}>
            <h3 className="reader-chapters-title">Chapitres</h3>
            {book.chapters.map((ch, i) => (
              <button key={ch.id} className={'reader-chapter-item ' + (i === currentChapter ? 'reader-chapter-item-active' : '')} onClick={() => goToChapter(i)}>
                <span className="reader-chapter-num">{i + 1}</span>
                {ch.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {chapter ? (
          <motion.div
            className="reader-content"
            key={currentChapter}
            initial={{
              opacity: 0,
              rotateY: turnDirection === 'next' ? 90 : -90,
              transformOrigin: turnDirection === 'next' ? 'left center' : 'right center',
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              rotateY: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              rotateY: turnDirection === 'next' ? -90 : 90,
              scale: 0.95,
            }}
            transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1],
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {currentChapter === 0 && book.authorNote && (
              <div className="reader-author-note">
                <p className="reader-author-note-label">📝 Note de l'auteur</p>
                <p className="reader-author-note-text">{book.authorNote}</p>
              </div>
            )}

            <h2 className="reader-chapter-title">{chapter.title}</h2>
            <p className="reader-chapter-time">⏱ Temps de lecture : {formatReadingTime(estimateReadingTime(chapter.content))}</p>
            <div className="reader-text" dangerouslySetInnerHTML={{ __html: renderRichText(chapter.content) }} />

            <div className="reader-nav">
              <button className="btn btn-secondary" disabled={isFirst} onClick={() => goToChapter(currentChapter - 1)}>
                <IoChevronBack /> Précédent
              </button>
              <span className="reader-nav-progress">{currentChapter + 1} / {book.chapters.length}</span>
              <button className="btn btn-primary" disabled={isLast && showFinished} onClick={() => {
                if (currentChapter + 1 >= book.chapters.length) {
                  setShowConfetti(true);
                  setShowFinished(true);
                } else {
                  goToChapter(currentChapter + 1);
                }
              }}>
                {isLast ? 'Terminer 🎉' : <>Suivant <IoChevronForward /></>}
              </button>
            </div>

            <AdBanner slot="reader-mid" format="horizontal" />

            {user && (
              <div className="reader-rating-section">
                <p className="reader-rating-label">Note ce livre</p>
                <StarRating bookId={bookId} ratings={book.ratings || {}} interactive={true} size="lg" />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <TipAuthor authorName={book.author} authorId={book.authorId?.replace('author_', '') || ''} />
                  <BoostBook bookId={bookId} bookTitle={book.title} />
                </div>
              </div>
            )}

            {user && <ChapterReactions bookId={bookId} chapterIndex={currentChapter} />}
            <Comments bookId={bookId} chapterId={chapter.id} bookAuthorId={book.authorId} bookTitle={book.title} />

            {showFinished && (
              <div className="reader-finished">
                <div className="reader-finished-emoji">🎉</div>
                <h2 className="reader-finished-title">Félicitations !</h2>
                <p className="reader-finished-text">Tu as terminé <strong>{book.title}</strong> !</p>
                <p className="reader-finished-sub">Si tu as aimé, tu aimeras aussi :</p>
                <Recommendations currentBookId={bookId} />
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            className="reader-content"
            key="no-chapter"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="reader-book-cover" style={{ background: book.coverGradient }}>
              <h2 style={{ color: 'white', fontFamily: 'var(--font-display)' }}>{book.title}</h2>
            </div>
            <p style={{ textAlign: 'center', marginTop: 16, opacity: 0.5 }}>Aucun chapitre disponible.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {showScrollTop && (
        <motion.button
          className="scroll-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          whileTap={{ scale: 0.9 }}
        >
          <IoArrowUp />
        </motion.button>
      )}
    </div>
  );
}